// Revise run — ONE slide type for the Kula Revise labs, four profiles
// (KULA-REVISE-LAB-ENGINE-PLAN.md §3). A run is one lesson, one knowledge
// check, one Practice Lab paper or one skeleton builder, played end to end
// inside this slide: cover → screens → results.
//
// The contract, scoring and validation all live in lib/revise; this file is
// layout, chrome and the two callbacks that reach the platform:
//   onActivityResponse — one row per assessed item (keyed by question id),
//                        one per Mark This Answer script, and one `__results`
//                        row on completion carrying the summary.
//   onComplete         — fired once, from the results screen, never from a
//                        screen's own data (the single completion point).
// Resume and the check's before/after comparison read back through
// services/reviseService.

import { useEffect, useMemo, useState } from 'react';
import type { ReviseRunSlideData, SlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type { ReviseRun, QuestionScreen as Q } from '../../../../../lib/revise/types';
import { REVISE_RESULTS_KEY, questionTimeSec } from '../../../../../lib/revise/types';
import { questionPayload, mtaPayload } from '../../../../../lib/revise/scoring';
import { validateRun, structuralIssues } from '../../../../../lib/revise/validate';
import { fetchReviseProgress, fetchReviseSibling } from '../../../../../services/reviseService';
import { REVISE_STYLES } from './styles';
import { useReviseRun, type ReviseRunApi } from './useReviseRun';
import { Carousel, Content, Frame, MtaIntro, Plan, Reveal, Rubric, Runsum, Source, SourceCard, Stat, Story } from './ContentScreens';
import { Question } from './QuestionScreen';
import { Builder, Evidence, Mta, Pick } from './ActivityScreens';
import { CoachButton, TimerChips } from './PaperChrome';
import { Results } from './Results';
import { WorkshopRunStudent } from './workshop/WorkshopPlayer';
import { SourcePaperRunStudent } from './sourcePaper/SourcePaperPlayer';
import { renderPairReview } from '../../../../../lib/revise/workshop/pair';

interface Props {
  data: ReviseRunSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (d: SlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  onAdvance?: () => void;
  /** Needed for resume and the before/after read; absent in previews. */
  slideId?: string;
  courseId?: string;
  /** Needed for the workshop's draft upserts; absent in previews. */
  lessonId?: string;
}

// ---------------------------------------------------------------------------
// Theme → tokens. The only place colours are decided.
// ---------------------------------------------------------------------------

function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0.5;
  const n = parseInt(m[1], 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/**
 * Practice Lab look (profiles 'paper', 'workshop' and 'source-paper'): the Kula Revise
 * identity the learner arrives from, navy band over an oatmeal canvas, Anton
 * display and Barlow text. The course theme still decides light or dark, and
 * correct/wrong stay the theme's green and red. Other profiles keep the course
 * theme untouched.
 */
const REVISE_PINK = '#d1185f';
const PAPER_PALETTE = {
  light: { bg: '#f2efe8', surface: '#ffffff', surface2: '#f7f5f0', line: '#ddd8cc', text: '#141b2d', text2: '#2c3550', muted: '#5b6273', band: '#141b2d', bandText: '#ffffff', bandMuted: '#cfd3dc', bandChip: '#2a3350', shadow: '#d6d1c4', coachBg: '#e3e6ee', coachLine: '#8e97ad' },
  dark: { bg: '#0f1422', surface: '#182033', surface2: '#1e2840', line: '#2e3a55', text: '#eef0f5', text2: '#c9cfdb', muted: '#9aa3b5', band: '#0a0e18', bandText: '#ffffff', bandMuted: '#aab2c3', bandChip: '#222b42', shadow: '#070a12', coachBg: '#1c2539', coachLine: '#56627f' },
};

export function reviseTokens(theme: Theme, accent?: string, profile?: ReviseRun['profile']): React.CSSProperties {
  const paper = profile === 'paper' || profile === 'workshop' || profile === 'source-paper';
  const dark = theme.mode === 'dark';
  const p = PAPER_PALETTE[dark ? 'dark' : 'light'];
  const c = paper
    ? { ...theme.colors, background: p.bg, surface: p.surface, text: p.text, textMuted: p.muted }
    : theme.colors;
  const valid = accent && /^#[0-9a-f]{6}$/i.test(accent) ? accent : undefined;
  const a = valid ?? (paper ? REVISE_PINK : c.accent || c.primary);
  const onAccent = luminance(a) > 0.45 ? '#0b0b0b' : '#ffffff';
  const vars: Record<string, string> = {
    '--rr-bg': c.background,
    '--rr-surface': c.surface,
    '--rr-surface-2': `color-mix(in srgb, ${c.text} ${dark ? 10 : 5}%, ${c.surface})`,
    '--rr-line': `color-mix(in srgb, ${c.text} ${dark ? 22 : 12}%, ${c.surface})`,
    '--rr-text': c.text,
    '--rr-text-2': `color-mix(in srgb, ${c.text} 82%, ${c.surface})`,
    '--rr-muted': c.textMuted,
    '--rr-accent': a,
    '--rr-on-accent': onAccent,
    '--rr-accent-soft': `color-mix(in srgb, ${a} ${dark ? 24 : 12}%, ${c.surface})`,
    '--rr-accent-ink': dark ? `color-mix(in srgb, ${a} 60%, white)` : `color-mix(in srgb, ${a} 85%, black)`,
    '--rr-chip-bg': `color-mix(in srgb, ${c.text} ${dark ? 16 : 8}%, ${c.surface})`,
    '--rr-chip-text': c.textMuted,
    '--rr-mark': `color-mix(in srgb, #f5d34b ${dark ? 35 : 55}%, ${c.surface})`,
    '--rr-ok-bg': c.feedback.correctBg,
    '--rr-ok-border': c.feedback.correctBorder,
    '--rr-ok-text': c.feedback.correctText,
    '--rr-warn-bg': c.feedback.warningBg,
    '--rr-warn-border': c.feedback.warningBorder,
    '--rr-warn-text': c.feedback.warningText,
    '--rr-no-bg': c.feedback.incorrectBg,
    '--rr-no-border': c.feedback.incorrectBorder,
    '--rr-no-text': c.feedback.incorrectText,
    '--rr-scrim': dark ? 'rgba(0,0,0,.6)' : 'rgba(20,20,20,.45)',
    '--rr-font-heading': theme.fonts.heading,
    '--rr-font-body': theme.fonts.body,
  };
  if (paper) {
    Object.assign(vars, {
      '--rr-surface-2': p.surface2,
      '--rr-line': p.line,
      '--rr-text-2': p.text2,
      '--rr-chip-bg': p.surface2,
      '--rr-chip-text': p.text2,
      '--rr-band': p.band,
      '--rr-band-text': p.bandText,
      '--rr-band-muted': p.bandMuted,
      '--rr-band-chip': p.bandChip,
      '--rr-band-accent': `color-mix(in srgb, ${a} 72%, white)`,
      '--rr-shadow': p.shadow,
      '--rr-coach-bg': p.coachBg,
      '--rr-coach-line': p.coachLine,
      '--rr-font-heading': 'Anton, Impact, sans-serif',
      '--rr-font-body': 'Barlow, system-ui, sans-serif',
    });
  }
  return vars as React.CSSProperties;
}

// ---------------------------------------------------------------------------

export function ReviseRunSlide(props: Props) {
  const run = props.data.run;
  const structural = useMemo(() => (run ? structuralIssues(run) : [{ code: 'schema.run', severity: 'block' as const, message: 'This slide has no run yet.' }]), [run]);
  if (props.isEditing) return <ReviseRunEditor {...props} structural={structural} />;
  if (structural.length || !run) {
    return (
      <div className="rr" style={reviseTokens(props.theme)}>
        <style>{REVISE_STYLES}</style>
        <div className="rr-stub">This activity is not ready yet.</div>
      </div>
    );
  }
  if (run.profile === 'source-paper' && run.sourcePaper) {
    return <SourcePaperRunStudent run={run} data={props.data} p={run.sourcePaper} tokens={reviseTokens(props.theme, run.accent, run.profile)} slideId={props.slideId} courseId={props.courseId} lessonId={props.lessonId} />;
  }
  if (run.profile === 'workshop' && run.workshop) {
    return <WorkshopRunStudent run={run} data={props.data} w={run.workshop} tokens={reviseTokens(props.theme, run.accent, run.profile)} onActivityResponse={props.onActivityResponse} slideId={props.slideId} courseId={props.courseId} lessonId={props.lessonId} />;
  }
  return <ReviseRunStudent {...props} run={run} />;
}

function ReviseRunStudent({ run, theme, onActivityResponse, onComplete, onAdvance, slideId, courseId }: Props & { run: ReviseRun }) {
  const [progress, setProgress] = useState<Record<string, Record<string, unknown>> | null | undefined>(undefined);
  const [sibling, setSibling] = useState<Record<string, unknown> | null>(null);

  // Resume + sibling reads: best-effort, never block first paint for long.
  useEffect(() => {
    let live = true;
    if (!slideId || !courseId) {
      setProgress(null);
      return;
    }
    fetchReviseProgress(courseId, slideId).then((p) => live && setProgress(p));
    if (run.check?.sitting === 'after') fetchReviseSibling(courseId, run.check.pairKey, 'before').then((s) => live && setSibling(s));
    return () => {
      live = false;
    };
  }, [slideId, courseId, run.check]);

  const emit = (responseData: Record<string, unknown>, isCorrect?: boolean, score?: number) =>
    onActivityResponse?.({ activityType: 'revise-run', mode: 'quiz', responseData, isCorrect, score });

  const api = useReviseRun({
    run,
    initialProgress: progress ?? null,
    onQuestionSubmit: (q, r, g) => emit(questionPayload(q, r, g), g.correct, g.marksTotal ? g.marksEarned / g.marksTotal : g.correct ? 1 : 0),
    onMtaCheck: (m, r) => emit(mtaPayload(m, r)),
    onResults: (summary, payload) => {
      emit(payload, undefined, summary.total ? summary.earned / summary.total : summary.pct / 100);
      onComplete?.();
    },
    onAdvance,
  });

  if (progress === undefined && slideId && courseId) {
    return (
      <div className="rr" style={reviseTokens(theme, run.accent, run.profile)} data-profile={run.profile}>
        <style>{REVISE_STYLES}</style>
        <div className="rr-stub">Loading…</div>
      </div>
    );
  }

  return (
    <div className="rr" style={reviseTokens(theme, run.accent, run.profile)} data-profile={run.profile} data-board={run.board}>
      <style>{REVISE_STYLES}</style>
      {api.phase === 'cover' && <Cover run={run} api={api} />}
      {(api.phase === 'run' || api.phase === 'review') && <Runner run={run} api={api} />}
      {api.phase === 'results' && (
        <div className="rr-body">
          <div className="rr-screen">
            <Results run={run} api={api} sibling={sibling} />
          </div>
        </div>
      )}
    </div>
  );
}

function Cover({ run, api }: { run: ReviseRun; api: ReviseRunApi }) {
  return (
    <div className="rr-body">
      <div className="rr-cover">
        <span className="rr-eyebrow">{run.cover.eyebrow}</span>
        <h1>{run.cover.title}</h1>
        <div className="rr-lines">
          {run.cover.lines.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </div>
        {run.cover.time && <div className="rr-time">{run.cover.time}</div>}
        <div>
          <button type="button" className="rr-btn-primary" onClick={api.begin}>
            {run.cover.begin}
          </button>
          {run.cover.skippable && api.onAdvance && (
            <button type="button" className="rr-btn-quiet" onClick={api.onAdvance}>
              Skip this one
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Runner({ run, api }: { run: ReviseRun; api: ReviseRunApi }) {
  const s = api.screen;
  const review = api.phase === 'review';
  const total = api.screens.length;
  const pos = review ? api.review.pos : api.idx;
  const count = review ? api.review.queue.length : total;
  const isPaper = run.profile === 'paper';
  const q = s?.type === 'question' ? (s as Q) : null;
  const paperSource = isPaper && q?.sourceKey ? run.sources?.find((x) => x.key === q.sourceKey) : undefined;
  const last = !review && api.idx + 1 >= total;

  return (
    <>
      <div className="rr-top">
        <span className="rr-eyebrow">{run.cover.eyebrow}</span>
        {isPaper && (
          <p className="rr-band-title">
            {run.cover.title.replace(/[.\s]+$/, '')}. <span>Practice Lab.</span>
          </p>
        )}
        {api.timed && q && <TimerChips q={q} qSeconds={api.qSeconds} totalSeconds={api.totalSeconds} recommended={questionTimeSec(q)} answered={!!api.answers[q.id]} timedMinutes={run.paper?.recommendedMinutes} />}
        <span className="rr-chip">{review ? `Wrong ${pos + 1} of ${count}` : `${pos + 1} / ${count}`}</span>
        <div className="rr-progress" aria-hidden>
          <i style={{ width: `${Math.round(((pos + 1) / Math.max(1, count)) * 100)}%` }} />
        </div>
      </div>
      <div className="rr-body">
        {review && <div className="rr-screen" style={{ paddingBottom: 0 }}><div className="rr-review-banner">Review mode: wrong question {pos + 1} of {count}. Your marks are unchanged; read the feedback on each one.</div></div>}
        <div className={`rr-screen${isPaper ? ' rr-wide' : ''}`} key={s?.id ?? 'none'}>
          {isPaper && q ? (
            <div className={`rr-paper${paperSource ? ' rr-has-source' : ''}`}>
              {paperSource && <SourceCard source={paperSource} run={run} compact />}
              <div style={{ display: 'grid', gap: 16 }}>
                {q.skill && <CoachButton skill={q.skill} run={run} />}
                <Question q={q} run={run} api={api} readOnly={review} />
              </div>
            </div>
          ) : (
            s && <Screen s={s} run={run} api={api} readOnly={review} />
          )}
        </div>
      </div>
      <div className="rr-nav">
        <button type="button" onClick={api.prev} disabled={pos === 0}>
          ← Back
        </button>
        <div className="rr-nav-right">
          {review ? (
            pos + 1 < count ? (
              <>
                <button type="button" onClick={api.exitReview}>
                  Back to results
                </button>
                <button type="button" className="rr-btn-primary" onClick={api.next}>
                  Next wrong →
                </button>
              </>
            ) : (
              <button type="button" className="rr-btn-primary" onClick={api.exitReview}>
                Back to results →
              </button>
            )
          ) : (
            <button type="button" className={api.canContinue ? 'rr-btn-primary' : ''} disabled={!api.canContinue} onClick={api.next}>
              {last ? 'See results →' : 'Continue →'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function Screen({ s, run, api, readOnly }: { s: NonNullable<ReviseRunApi['screen']>; run: ReviseRun; api: ReviseRunApi; readOnly: boolean }) {
  switch (s.type) {
    case 'story':
      return <Story s={s} />;
    case 'content':
      return <Content s={s} />;
    case 'stat':
      return <Stat s={s} />;
    case 'source':
      return <Source s={s} run={run} />;
    case 'carousel':
      return <Carousel s={s} />;
    case 'runsum':
      return <Runsum s={s} run={run} api={api} />;
    case 'reveal':
      return <Reveal s={s} api={api} />;
    case 'rubric':
      return <Rubric s={s} />;
    case 'plan':
      return <Plan s={s} />;
    case 'builder':
      return <Builder s={s} api={api} />;
    case 'mta-intro':
      return <MtaIntro s={s} />;
    case 'mta':
      return <Mta s={s} api={api} />;
    case 'question':
      return <Question q={s} run={run} api={api} readOnly={readOnly} />;
    case 'pick':
      return <Pick s={s} api={api} />;
    case 'evidence':
      return <Evidence s={s} api={api} />;
    case 'frame':
      return <Frame s={s} run={run} api={api} />;
  }
}

// ---------------------------------------------------------------------------
// Editor — the author's view. Runs are written by the MCP (or a future panel);
// the builder shows what arrived, what the validator says, and lets the author
// fix the cover, the accent and, as a last resort, the raw JSON.
// ---------------------------------------------------------------------------

function ReviseRunEditor({ data, theme, onUpdate, structural }: Props & { structural: ReturnType<typeof structuralIssues> }) {
  const run = data.run;
  const validation = useMemo(() => (run && structural.length === 0 ? validateRun(run) : null), [run, structural]);
  const [showJson, setShowJson] = useState(false);
  const [json, setJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const update = (patch: Partial<ReviseRun>) => {
    if (!run) return;
    const next = { ...run, ...patch };
    onUpdate({ ...data, run: next, title: next.cover.title, subtitle: next.cover.eyebrow });
  };
  const updateCover = (patch: Partial<ReviseRun['cover']>) => run && update({ cover: { ...run.cover, ...patch } });

  const applyJson = () => {
    try {
      const parsed = JSON.parse(json);
      const issues = structuralIssues(parsed);
      if (issues.length) {
        setJsonError(issues.map((i) => i.message).join(' '));
        return;
      }
      setJsonError(null);
      onUpdate({ ...data, run: parsed as ReviseRun, title: (parsed as ReviseRun).cover.title, subtitle: (parsed as ReviseRun).cover.eyebrow });
      setShowJson(false);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const counts = run
    ? run.screens.reduce<Record<string, number>>((acc, s) => {
        acc[s.type] = (acc[s.type] ?? 0) + 1;
        return acc;
      }, {})
    : {};

  return (
    <div className="rr" style={reviseTokens(theme, run?.accent)}>
      <style>{REVISE_STYLES}</style>
      <div className="rr-body">
        <div className="rr-screen" style={{ gap: 14 }}>
          <span className="rr-eyebrow">Revise run · {run ? `${run.profile} · ${run.board.toUpperCase()}` : 'no run'}</span>
          {!run || structural.length > 0 ? (
            <div className="rr-feedback rr-fb-no">
              <b>This slide has no valid run.</b>
              {structural.map((i) => (
                <div key={i.code + i.message}>{i.message}</div>
              ))}
            </div>
          ) : (
            <>
              <div className="rr-card" style={{ display: 'grid', gap: 10 }}>
                <label>
                  <span className="rr-eyebrow">Eyebrow</span>
                  <input className="rr-input" value={run.cover.eyebrow} onChange={(e) => updateCover({ eyebrow: e.target.value })} style={inputStyle} />
                </label>
                <label>
                  <span className="rr-eyebrow">Title</span>
                  <input value={run.cover.title} onChange={(e) => updateCover({ title: e.target.value })} style={inputStyle} />
                </label>
                <label>
                  <span className="rr-eyebrow">Hook lines (one per line)</span>
                  <textarea value={run.cover.lines.join('\n')} onChange={(e) => updateCover({ lines: e.target.value.split('\n') })} rows={3} style={inputStyle} />
                </label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ flex: 1 }}>
                    <span className="rr-eyebrow">Button</span>
                    <input value={run.cover.begin} onChange={(e) => updateCover({ begin: e.target.value })} style={inputStyle} />
                  </label>
                  <label style={{ flex: 1 }}>
                    <span className="rr-eyebrow">Time line</span>
                    <input value={run.cover.time ?? ''} onChange={(e) => updateCover({ time: e.target.value })} style={inputStyle} />
                  </label>
                  <label>
                    <span className="rr-eyebrow">Accent</span>
                    <input type="color" value={run.accent ?? '#5b3fd1'} onChange={(e) => update({ accent: e.target.value })} style={{ ...inputStyle, padding: 2, width: 64 }} />
                  </label>
                </div>
              </div>

              <div className="rr-card">
                <span className="rr-eyebrow">Contents</span>
                <div className="rr-chips" style={{ marginTop: 8 }}>
                  {Object.entries(counts).map(([t, n]) => (
                    <span key={t} className="rr-chip">
                      {n} × {t}
                    </span>
                  ))}
                  {validation && <span className="rr-chip rr-chip-accent">{validation.report.assessed} assessed · {validation.report.marksTotal} marks</span>}
                </div>
              </div>

              {run.profile === 'workshop' && run.workshop && (
                <details className="rr-card rr-terms">
                  <summary>Pair sentences: what the engine writes for each pair of case studies</summary>
                  <pre style={{ whiteSpace: 'pre-wrap', font: 'inherit', fontSize: 13, lineHeight: 1.5, margin: '8px 0 0' }}>{renderPairReview(run.workshop)}</pre>
                </details>
              )}
              {validation && (
                <div className="rr-card" style={{ display: 'grid', gap: 8 }}>
                  <span className="rr-eyebrow">Validator</span>
                  {validation.blocks.length === 0 && validation.warnings.length === 0 && <div className="rr-feedback rr-fb-ok">No issues.</div>}
                  {validation.blocks.map((i, n) => (
                    <div key={n} className="rr-feedback rr-fb-no">
                      <b>Block · {i.code}</b>
                      {i.message}
                    </div>
                  ))}
                  {validation.warnings.map((i, n) => (
                    <div key={n} className="rr-feedback rr-fb-neutral">
                      <b>Warning · {i.code}</b>
                      {i.message}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div>
            <button
              type="button"
              className="rr-btn-quiet"
              onClick={() => {
                setJson(JSON.stringify(run ?? {}, null, 2));
                setShowJson((v) => !v);
              }}
            >
              {showJson ? 'Hide raw JSON' : 'Edit raw JSON'}
            </button>
          </div>
          {showJson && (
            <div style={{ display: 'grid', gap: 8 }}>
              <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={18} style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} spellCheck={false} />
              {jsonError && <div className="rr-feedback rr-fb-no">{jsonError}</div>}
              <div>
                <button type="button" className="rr-btn-primary" onClick={applyJson}>
                  Apply
                </button>
              </div>
            </div>
          )}
          <p className="rr-note">Switch to Preview to play the run as a learner. Results are recorded only in a live lesson.</p>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid var(--rr-line)',
  background: 'var(--rr-surface)',
  color: 'var(--rr-text)',
  font: 'inherit',
};

export { REVISE_RESULTS_KEY };
